import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { TopBar } from "../components/UI";

const API = process.env.REACT_APP_API_URL || "https://g6fw4rjkvb.execute-api.eu-central-1.amazonaws.com/prototype";

// Landing page for the emailed reset link: /reset-password?token=…&email=…
// On load it asks the API whether this (email, token) pair is valid and
// unexpired (2 hours, single use). Only then is the "new password" form
// shown. After a successful reset the user is sent to the root landing page.
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const email = searchParams.get("email") || "";

  const [status, setStatus] = useState("checking"); // checking | valid | invalid
  const [linkError, setLinkError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token || !email) { setStatus("invalid"); setLinkError("This reset link is incomplete."); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/auth/reset-password/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, token }),
        });
        const d = await r.json();
        if (cancelled) return;
        if (!r.ok) throw new Error(d.error || "This reset link is invalid or has expired.");
        setStatus("valid");
      } catch (e) {
        if (cancelled) return;
        setLinkError(e.message); setStatus("invalid");
      }
    })();
    return () => { cancelled = true; };
  }, [token, email]);

  const submit = async () => {
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, token, password }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Failed to reset password");
      setDone(true);
      setTimeout(() => navigate("/"), 2000);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const labelStyle = { fontSize: 10, fontWeight: 600, color: "#6A7E58", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 4 };
  const inputStyle = { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #C4D0A8", background: "#EDF1E4", color: "#2C3820", fontSize: 13, boxSizing: "border-box" };
  const canSubmit = !loading && password && confirm;

  return (
    <div style={{ minHeight: "100vh", background: "#F2F5EC" }}>
      <TopBar title="Wine4Collect" />
      <div style={{ maxWidth: 420, margin: "60px auto", padding: "0 16px" }}>
        <div style={{ background: "white", borderRadius: 12, padding: 32, border: "1px solid #C4D0A8" }}>
          {status === "checking" && (
            <p style={{ textAlign: "center", fontSize: 13, color: "#6A7E58", margin: 0 }}>Checking your reset link…</p>
          )}

          {status === "invalid" && (
            <div style={{ textAlign: "center" }}>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: "#8B1A2F", margin: "0 0 8px" }}>Link not valid</h2>
              <p style={{ fontSize: 13, color: "#6A7E58", lineHeight: 1.5 }}>{linkError || "This reset link is invalid or has expired."}</p>
              <Link to="/forgot-password" state={{ email }}
                style={{ display: "inline-block", marginTop: 12, padding: "10px 20px", borderRadius: 8, background: "#3D4A1A", color: "white", fontWeight: 600, fontSize: 13, textDecoration: "none" }}>
                Request a new link
              </Link>
            </div>
          )}

          {status === "valid" && done && (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>✓</div>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: "#3D4A1A", margin: "0 0 8px" }}>Password updated</h2>
              <p style={{ fontSize: 13, color: "#6A7E58" }}>Redirecting…</p>
            </div>
          )}

          {status === "valid" && !done && (
            <>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, color: "#1E2C14", margin: "0 0 8px" }}>Set a new password</h2>
              <p style={{ fontSize: 13, color: "#6A7E58", marginBottom: 24 }}>
                Choose a new password for <strong>{email}</strong>.
              </p>
              {error && <div style={{ background: "rgba(139,26,47,0.08)", color: "#8B1A2F", padding: "10px 12px", borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Email</label>
                <input type="email" value={email} readOnly style={{ ...inputStyle, opacity: 0.7 }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>New password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters" style={inputStyle} />
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Confirm password</label>
                <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && canSubmit && submit()} style={inputStyle} />
              </div>
              <button onClick={submit} disabled={!canSubmit}
                style={{ width: "100%", padding: "11px", borderRadius: 8, border: "none", background: "#3D4A1A", color: "white", fontWeight: 600, fontSize: 14, cursor: "pointer", opacity: canSubmit ? 1 : 0.6 }}>
                {loading ? "Saving…" : "Set new password"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
